/**
 * useNewItemHighlight - Hook to track and highlight newly added items
 *
 * Automatically applies a shimmer-highlight effect to newly added items
 * for a brief period (default 3 seconds) to provide visual feedback.
 *
 * Usage:
 * ```typescript
 * const { isNew, markAsNew } = useNewItemHighlight();
 *
 * // When adding a new item:
 * markAsNew(itemId);
 *
 * // When rendering items:
 * <div className={isNew(itemId) ? 'shimmer-highlight' : ''}>
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface NewItemHighlightOptions {
  duration?: number; // Duration in milliseconds (default: 3000)
}

export function useNewItemHighlight(options: NewItemHighlightOptions = {}) {
  const { duration = 3000 } = options;
  const [newItems, setNewItems] = useState<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  /**
   * Mark an item as new (will show highlight)
   */
  const markAsNew = useCallback((itemId: string) => {
    // Clear existing timeout for this item if any
    const existingTimeout = timeoutsRef.current.get(itemId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Add to new items set
    setNewItems(prev => new Set(prev).add(itemId));

    // Schedule removal after duration
    const timeout = setTimeout(() => {
      setNewItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      timeoutsRef.current.delete(itemId);
    }, duration);

    timeoutsRef.current.set(itemId, timeout);
  }, [duration]);

  /**
   * Check if an item is new (should show highlight)
   */
  const isNew = useCallback((itemId: string): boolean => {
    return newItems.has(itemId);
  }, [newItems]);

  /**
   * Manually remove highlight from an item
   */
  const clearNew = useCallback((itemId: string) => {
    const timeout = timeoutsRef.current.get(itemId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(itemId);
    }
    setNewItems(prev => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  /**
   * Clear all highlights
   */
  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
    setNewItems(new Set());
  }, []);

  return {
    isNew,
    markAsNew,
    clearNew,
    clearAll
  };
}
